'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, MapPin, Phone } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormControl, FormField, FormItem } from '@kit/ui/form';

import {
  type OrganizationProfile,
  OrganizationProfileSchema,
} from '../../schema/professional-profile.schema';
import {
  IconInput,
  RequiredFormLabel,
  WizardFormFooter,
} from './form-components';
import { useProfileFormSubmit } from './use-profile-form-submit';

interface OrganizationDetailsStepProps {
  userType: 'pharma' | 'medical_place' | 'hospital';
  userId: string;
  onBack: () => void;
  onComplete: () => void;
}

export function OrganizationDetailsStep({
  userType,
  userId,
  onBack,
  onComplete,
}: OrganizationDetailsStepProps) {
  const { t } = useTranslation('account');
  const { submitProfile } = useProfileFormSubmit(onComplete);

  const form = useForm<OrganizationProfile>({
    resolver: zodResolver(OrganizationProfileSchema),
    defaultValues: {
      userType,
      organizationName: '',
      phoneNumber: '',
      address: '',
    },
  });

  const onSubmit = (data: OrganizationProfile) =>
    submitProfile({
      userId,
      userType: data.userType,
      publicData: {
        name: data.organizationName,
        phone_number: data.phoneNumber,
        address: data.address,
      },
    });

  return (
    <div className="space-y-6 pt-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="organizationName"
            render={({ field }) => (
              <FormItem>
                <RequiredFormLabel>
                  {t('profileWizard.organizationName')}
                </RequiredFormLabel>
                <FormControl>
                  <IconInput
                    icon={Building2}
                    {...field}
                    placeholder="Nazwa firmy Sp. z o.o."
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <RequiredFormLabel>
                  {t('profileWizard.phoneNumber')}
                </RequiredFormLabel>
                <FormControl>
                  <IconInput
                    icon={Phone}
                    {...field}
                    placeholder="601 000 000"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <RequiredFormLabel>
                  {t('profileWizard.address')}
                </RequiredFormLabel>
                <FormControl>
                  <IconInput
                    icon={MapPin}
                    {...field}
                    placeholder={t('profileWizard.addressPlaceholder')}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <WizardFormFooter
            onBack={onBack}
            isSubmitting={form.formState.isSubmitting}
          />
        </form>
      </Form>
    </div>
  );
}
